/**
 * Remembering which context this browser last signed in as.
 *
 * The picker is by design shown on EVERY sign-in for an account linked to more
 * than one institute (the choice is fixed for the session; changing it means
 * signing in again). For the owner who also teaches elsewhere, or the parent of
 * two children, that is the same tap every single day. This module does not
 * remove the tap - the choice must stay visible and reversible - but it puts
 * the previous answer first, marks it "Last used" and focuses it, so the common
 * case is one tap or just Enter.
 *
 * Keyed by email, per browser: two people sharing a machine do not inherit
 * each other's choice. Never authoritative - the server re-validates every
 * selection, so a remembered uid that has since been revoked simply is not in
 * the list any more and is ignored.
 */

import { jwtDecode } from 'jwt-decode';

const KEY_PREFIX = 'last_login_context:';

const normalizeEmail = (email: string | null | undefined): string =>
  (email ?? '').trim().toLowerCase();

const keyFor = (email: string | null | undefined): string | null => {
  const e = normalizeEmail(email);
  return e ? `${KEY_PREFIX}${e}` : null;
};

export const readLastContext = (email: string | null | undefined): string | null => {
  const key = keyFor(email);
  if (!key) return null;
  try {
    return localStorage.getItem(key) || null;
  } catch {
    return null;
  }
};

export const rememberLastContext = (
  email: string | null | undefined,
  membershipUid: string,
): void => {
  const key = keyFor(email);
  if (!key || !membershipUid) return;
  try {
    localStorage.setItem(key, membershipUid);
  } catch {
    // Storage blocked - the picker simply shows unordered next time.
  }
};

/**
 * The remembered context first, the rest in the server's order. Pure, so the
 * dialogs can call it on every render.
 */
export const orderByLastUsed = <T extends { membership_uid: string }>(
  contexts: T[],
  lastUsedUid: string | null | undefined,
): T[] => {
  if (!lastUsedUid) return contexts;
  const index = contexts.findIndex((c) => c.membership_uid === lastUsedUid);
  if (index <= 0) return contexts;
  return [contexts[index], ...contexts.slice(0, index), ...contexts.slice(index + 1)];
};

/** The email claim of an access token, or null. Not a security check. */
export const emailFromAccessToken = (token: string | null | undefined): string | null => {
  if (!token) return null;
  try {
    const payload = jwtDecode<{ email?: string }>(token);
    return payload?.email ? String(payload.email) : null;
  } catch {
    return null;
  }
};

/** Email of the session whose tokens are currently stored, or null. */
export const currentSessionEmail = (): string | null => {
  try {
    return emailFromAccessToken(
      localStorage.getItem('access_token') || sessionStorage.getItem('access_token'),
    );
  } catch {
    return null;
  }
};
